import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { GraphQLClient } from 'graphql-request'
import { RequestInit } from 'graphql-request/dist/types.dom'
import { useMemo } from 'react'

import { API_ENDPOINT } from '../constants'

// Type of the hook that is used to get data from the API.
export type UseQueryDataHookType = <TError, TData, TVariables>(
  client: GraphQLClient,
  variables: TVariables,
  options?: UseQueryOptions<TData, TError, TData>,
  headers?: RequestInit['headers']
) => UseQueryResult<TData, TError>

// TODO: Start replacing our "get" mutations with useQueryData and rename useCreateModifyMutation and start
//       using it for our "set" mutations.
//       https://github.com/UnreservedInc/unreserved-frontend-v2/issues/2629
export function useQueryData<TError, TData, TVariables>(
  getDataHook: UseQueryDataHookType,
  variables: TVariables,
  options: UseQueryOptions<TData, TError, TData> = {}
) {
  const { data, isFetching, refetch, fetchStatus, isError, isSuccess, isLoading } = getDataHook<
    TError,
    TData,
    TVariables
  >(new GraphQLClient(API_ENDPOINT), { ...variables }, { ...options })

  // ------------------------------------------------------------------------------------------------------------
  //
  // NOTE:  Use useMemo() because we return an object from this hook and and if we return a new object
  //        every time (even when the data doesn't change) any useEffects() that are dependent on this data
  //        will re-run and potentially cause infinite render loops.
  //
  // ------------------------------------------------------------------------------------------------------------
  return useMemo(() => {
    return {
      data,
      refetch,
      fetchStatus,
      isError,
      isSuccess,

      // Reference: https://tanstack.com/query/v4/docs/framework/react/guides/queries
      isLoading, // True if the query has no data yet.
      isFetching, // True if the query is fetching at any time (including background refetching).
    } as UseQueryResult<TData, TError>
  }, [data, fetchStatus, isError, isFetching, isLoading, isSuccess, refetch])
}
